import {
  PUBLIC_ALLOCATION_BUCKET_VALUES,
  PUBLIC_PERFORMANCE_DIRECTION_VALUES,
  PUBLIC_PET_HAT_VALUES,
  PUBLIC_PET_PALETTE_VALUES,
  PUBLIC_PET_PATTERN_VALUES,
  PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION,
  PUBLIC_ROOM_WEATHER_VALUES,
  SHARE_ALLOCATION_VISIBILITY_VALUES,
  SHARE_HISTORY_VISIBILITY_VALUES,
  SHARE_IDENTITY_VISIBILITY_VALUES,
  SHARE_PERFORMANCE_VISIBILITY_VALUES,
} from "./constants";

export type ShareIdentityVisibilityV1 =
  (typeof SHARE_IDENTITY_VISIBILITY_VALUES)[number];
export type SharePerformanceVisibilityV1 =
  (typeof SHARE_PERFORMANCE_VISIBILITY_VALUES)[number];
export type ShareAllocationVisibilityV1 =
  (typeof SHARE_ALLOCATION_VISIBILITY_VALUES)[number];
export type ShareHistoryVisibilityV1 =
  (typeof SHARE_HISTORY_VISIBILITY_VALUES)[number];

export interface ShareVisibilityPolicyV1 {
  readonly identity: ShareIdentityVisibilityV1;
  readonly performance: SharePerformanceVisibilityV1;
  readonly allocation: ShareAllocationVisibilityV1;
  readonly history: ShareHistoryVisibilityV1;
}

/**
 * The product default is a room-only share. Pet count and room weather remain
 * visible, while identity and per-pet financial signals do not exist in the
 * public payload.
 */
export const DEFAULT_SHARE_VISIBILITY_POLICY_V1: ShareVisibilityPolicyV1 =
  Object.freeze({
    identity: "hidden",
    performance: "hidden",
    allocation: "hidden",
    history: "none",
  });

export type PublicRoomWeatherV1 =
  (typeof PUBLIC_ROOM_WEATHER_VALUES)[number];
export type PublicPerformanceDirectionV1 =
  (typeof PUBLIC_PERFORMANCE_DIRECTION_VALUES)[number];
export type PublicAllocationBucketV1 =
  (typeof PUBLIC_ALLOCATION_BUCKET_VALUES)[number];
export type PublicPetPaletteV1 =
  (typeof PUBLIC_PET_PALETTE_VALUES)[number];
export type PublicPetPatternV1 =
  (typeof PUBLIC_PET_PATTERN_VALUES)[number];
export type PublicPetHatV1 =
  (typeof PUBLIC_PET_HAT_VALUES)[number];

export interface PublicPetAppearanceV1 {
  readonly paletteId: PublicPetPaletteV1;
  readonly patternId: PublicPetPatternV1;
  readonly hatId?: PublicPetHatV1;
}

/**
 * A direction-only policy never carries `roundedPercent`. Under the rounded
 * policy it is an integer percentage point, capped to [-100, 100]. Unknown
 * observations omit the number rather than manufacturing zero.
 */
export interface PublicPerformanceSignalV1 {
  readonly direction: PublicPerformanceDirectionV1;
  readonly roundedPercent?: number;
}

export interface PublicRoomPetV1 {
  /** Session-scoped pseudonymous ID; never a local holding/instrument ID. */
  readonly id: string;
  readonly appearance: PublicPetAppearanceV1;
  readonly ticker?: string;
  readonly name?: string;
  readonly logoKey?: string;
  readonly performance?: PublicPerformanceSignalV1;
  readonly allocationBucket?: PublicAllocationBucketV1;
}

export interface PublicRoomStateV1 {
  /**
   * Weather is the one deliberately retained room-level derived signal in
   * room-only mode. Exact return, intensity, value, and coverage are absent.
   */
  readonly weather: PublicRoomWeatherV1;
}

export interface PublicRoomHistoryPetV1 {
  readonly id: string;
  readonly performance?: PublicPerformanceSignalV1;
  readonly allocationBucket?: PublicAllocationBucketV1;
}

export interface PublicRoomHistoryPointV1 {
  readonly marketDate: string;
  readonly room: PublicRoomStateV1;
  readonly pets: readonly PublicRoomHistoryPetV1[];
}

export interface PublicRoomHistoryV1 {
  readonly range: Exclude<ShareHistoryVisibilityV1, "none">;
  readonly points: readonly PublicRoomHistoryPointV1[];
}

/**
 * Deliberately minimal public DTO. Exact balances, cost, quantity, transaction
 * data, local portfolio/holding identity, and owner credentials have no field
 * in this type and therefore cannot be "accidentally unhidden" by the UI.
 */
export interface PublicRoomSnapshotV1 {
  readonly schemaVersion: typeof PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION;
  readonly rendererVersion: string;
  /** A random value generated for this share session, not the scene seed. */
  readonly publicSeed: string;
  readonly visibility: ShareVisibilityPolicyV1;
  readonly room: PublicRoomStateV1;
  readonly pets: readonly PublicRoomPetV1[];
  readonly history?: PublicRoomHistoryV1;
}
